import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { compoundsDB } from '../data/compounds';
import { mergeOverrides, loadOverrides, clearOverrides, getOverrideStats } from '../data/localOverrides';
import { useWorkSession } from '../context/WorkSessionContext';
import { listUsers, inviteUser, updateUserRole, deleteUser, getResetLink } from '../services/apiService';
import './Admin.css';

// ─── EXPORT HELPERS ──────────────────────────────────────────────────────────

function safeStr(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(item =>
    typeof item === 'string' ? item :
    (item?.title || item?.task || item?.description || JSON.stringify(item))
  ).join(' | ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function buildMasterRows(db) {
  return Object.values(db).map(c => ({
    'WOHLER_UID':            (c.id || '').toUpperCase().replace(/-/g, '_'),
    'Compound ID (slug)':    c.id || '',
    'Short Name':            c.name || '',
    'Full / IUPAC Name':     c.fullName || '',
    'Aliases / Trade Names': safeStr(c.aliases),
    'Category ID':           c.categoryId || '',
    'Research Program':      c.program || '',
    'CAS Number':            c.cas || '',
    'Molecular Formula':     c.formula || '',
    'Mol. Weight (g/mol)':   c.molecularWeight || c.mw || '',
    'Scheduling Status':     c.schedulingStatus || '',
    'Scheduling Note':       c.schedulingNote || '',
    'Benefit Score (0-100)': c.benefitScore ?? '',
    'Harm Score (0-100)':    c.harmScore ?? '',
    'Evidence Confidence':   c.evidenceConfidence || '',
    'Status':                c.status || '',
    'Status Color':          c.statusColor || '',
    'GO Decision':           c.goDecision || '',
    'Priority Tier':         c.priorityTier || '',
    'Summary':               c.summary || c.description || '',
    'Research Advantages':   safeStr(c.pros),
    'Research Considerations': safeStr(c.cons),
    'Flag / Warning':        c.flagWarning || '',
    // Pharmacology
    'Pharmacology: Mechanism':       c.pharmacology?.mechanism || c.mechanismSummary || '',
    'Pharmacology: Primary Target':  c.pharmacology?.primaryTarget || '',
    'Pharmacology: Targets (Ki/Emax)': safeStr(c.pharmacology?.targets),
    'Pharmacology: Onset/Duration':  c.pharmacology?.onsetDuration || c.pharmacology?.duration || '',
    'Pharmacology: Metabolism':      c.pharmacology?.metabolism || '',
    'PK: Oral Bioavailability':      c.pharmacology?.bioavailability || c.pharmacology?.pkBioavailability || '',
    'PK: Tmax':                      c.pharmacology?.tmax || c.pharmacology?.pkTmax || '',
    'PK: Half-life':                 c.pharmacology?.halfLife || c.pharmacology?.pkHalfLife || '',
    // Lab synthesis
    'Lab Synthesis: Overview':       c.labSynthesis?.overview || c.labSynthesis?.title || '',
    'Lab Synthesis: Route Family':   c.labSynthesis?.routeFamily || '',
    'Lab Synthesis: Steps':          safeStr(c.labSynthesis?.steps),
    'Lab Synthesis: Overall Yield':  c.labSynthesis?.overallYield || c.labSynthesis?.yield || '',
    'Lab Synthesis: Analytics':      c.labSynthesis?.analytics || '',
    // Kg mfg
    'Kg Mfg: Overview':             c.kgScaleSynthesis?.overview || '',
    'Kg Mfg: Feasibility':          c.kgScaleSynthesis?.feasibility || '',
    'Kg Mfg: Target Scale':         c.kgScaleSynthesis?.targetScale || '',
    'Kg Mfg: Solvents Used':        safeStr(c.kgScaleSynthesis?.standardSolventsUsed || c.kgScaleSynthesis?.solventsUsed),
    'Kg Mfg: Solvents Avoided':     safeStr(c.kgScaleSynthesis?.standardSolventsNotUsed),
    'Kg Mfg: Procedure (Steps)':    safeStr(c.kgScaleSynthesis?.steps || c.kgScaleSynthesis?.procedure),
    'Kg Mfg: Hazards/EHS':          safeStr(c.kgScaleSynthesis?.hazards),
    'Kg Mfg: Yield Target':         c.kgScaleSynthesis?.yieldTarget || '',
    'Kg Mfg: Cost Estimate ($/kg)': c.kgScaleSynthesis?.costEstimate || c.kgScaleSynthesis?.estCostPerKg || '',
    'Kg Mfg: Campaign Timeline':    c.kgScaleSynthesis?.timeline || '',
    // Safety
    'Safety: Acute Toxicity':       c.safety?.acuteToxicity || '',
    'Safety: Chronic Risks':        c.safety?.chronicRisks || '',
    'Safety: EHS Classification':   c.safety?.ehsClassification || '',
    'Safety: Toxicology':           c.safety?.toxicology || '',
    'Safety: Side Effects':         safeStr(c.safety?.sideEffects || c.sideEffects),
    'Safety: PPE Requirements':     c.safety?.ppeRequired || '',
    'Safety: Contraindications':    c.safety?.contraindications || '',
    // Scoring
    'WS: Benefit Score':            c.wohlerScoring?.benefit ?? '',
    'WS: Harm Score':               c.wohlerScoring?.harm ?? '',
    'WS: Net Value':                c.wohlerScoring?.netValue ?? '',
    'WS: Evidence Confidence':      c.wohlerScoring?.evidenceConfidence || '',
    'WS: Scoring Rationale':        c.wohlerScoring?.rationale || '',
    // Research
    'References':                   safeStr(c.publishedResearch?.citations || c.references || c.sources),
    'Next Steps / To-Do':           safeStr(c.nextSteps),
    'Key Research Questions':       safeStr(c.keyResearchQuestions),
    'Notes':                        c.internalNotes || c.notes || '',
  }));
}

function exportToExcel(db) {
  const rows = buildMasterRows(db);
  const wb = XLSX.utils.book_new();

  // ── MASTER sheet ──────────────────────────────────────────────────────────
  const ws = XLSX.utils.json_to_sheet(rows);

  // Column widths
  const colWidths = Object.keys(rows[0] || {}).map(k => ({
    wch: Math.min(60, Math.max(18, k.length + 4))
  }));
  ws['!cols'] = colWidths;
  XLSX.utils.book_append_sheet(wb, ws, 'MASTER');

  // ── INSTRUCTIONS sheet ────────────────────────────────────────────────────
  const instRows = [
    ['Wohler Compound Database — Import/Export Guide'],
    [''],
    ['HOW TO UPDATE COMPOUND DATA'],
    ['1. Edit any cells in the MASTER sheet'],
    ['2. Save the file as .xlsx'],
    ['3. Go to wohler.ai/admin'],
    ['4. Drag & drop the saved file onto the Import section'],
    ['5. Review the preview, then click Apply Import'],
    ['6. Changes take effect immediately — no rebuild needed'],
    [''],
    ['RULES'],
    ['• WOHLER_UID — Primary key. Never change this column.'],
    ['• Compound ID (slug) — URL slug (lowercase-hyphen). Never change.'],
    ['• Blank cells are skipped — they inherit the existing value.'],
    ['• Arrays (Aliases, Steps, Solvents, Next Steps) — separate items with a pipe: Item 1 | Item 2 | Item 3'],
    ['• References — format as:  Title (https://url.com) | Title 2 (https://url2.com)'],
    ['• Scores — plain numbers only (0–100)'],
    ['• Status Color — must be: green, orange, or red'],
    ['• Scheduling Status — must be: unscheduled, scheduled, or monitor'],
    [''],
    ['COLUMN GUIDE'],
    ['WOHLER_UID', 'Primary key — DO NOT CHANGE'],
    ['Compound ID (slug)', 'URL slug — DO NOT CHANGE'],
    ['Short Name', 'Display name shown on cards and headers'],
    ['Full / IUPAC Name', 'Full chemical name'],
    ['Aliases / Trade Names', 'Pipe-separated: Name1 | Name2 | Name3'],
    ['Category ID', 'Must match a category ID in categories.js'],
    ['Research Program', 'Program label (e.g. Program B — Tryptamines)'],
    ['CAS Number', 'CAS registry number'],
    ['Molecular Formula', 'e.g. C13H18N2O'],
    ['Mol. Weight (g/mol)', 'Numeric molecular weight'],
    ['Scheduling Status', 'unscheduled | scheduled | monitor'],
    ['Scheduling Note', 'Free text — legal/regulatory notes'],
    ['Benefit Score (0-100)', 'Wohler benefit score integer'],
    ['Harm Score (0-100)', 'Wohler harm score integer'],
    ['Evidence Confidence', 'HIGH | MODERATE | LOW'],
    ['Status', 'Card status label (e.g. Strongly Favored)'],
    ['Status Color', 'green | orange | red'],
    ['GO Decision', 'GO / NO-GO / HOLD decision text'],
    ['Summary', 'Short summary shown on compound card'],
    ['Pharmacology: Mechanism', 'MOA description'],
    ['Kg Mfg: Solvents Used', 'Pipe-separated Wohler standard solvents'],
    ['Safety: PPE Requirements', 'PPE needed for lab handling'],
    ['Next Steps / To-Do', 'Pipe-separated action items'],
  ];
  const wsInst = XLSX.utils.aoa_to_sheet(instRows);
  wsInst['!cols'] = [{ wch: 35 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, wsInst, 'INSTRUCTIONS');

  // Write and trigger download
  XLSX.writeFile(wb, `Wohler_Compounds_${new Date().toISOString().slice(0,10)}.xlsx`);
}

// ─── IMPORT HELPERS ───────────────────────────────────────────────────────────

const ARRAY_COLS = new Set([
  'aliases / trade names',
  'lab synthesis: steps',
  'kg mfg: solvents used',
  'kg mfg: solvents avoided',
  'kg mfg: procedure (steps)',
  'next steps / to-do',
  'key research questions',
  'safety: side effects',
]);

const NUMERIC_COLS = new Set([
  'benefit score (0-100)',
  'harm score (0-100)',
  'mol. weight (g/mol)',
  'ws: benefit score',
  'ws: harm score',
  'ws: net value',
]);

// Map Excel column header → compound field path
// Format: "fieldName" or "parent.fieldName"
const COL_MAP = {
  'short name':                    'name',
  'full / iupac name':             'fullName',
  'aliases / trade names':         'aliases',
  'category id':                   'categoryId',
  'research program':              'program',
  'cas number':                    'cas',
  'molecular formula':             'formula',
  'mol. weight (g/mol)':           'mw',
  'scheduling status':             'schedulingStatus',
  'scheduling note':               'schedulingNote',
  'benefit score (0-100)':         'benefitScore',
  'harm score (0-100)':            'harmScore',
  'evidence confidence':           'evidenceConfidence',
  'status':                        'status',
  'status color':                  'statusColor',
  'go decision':                   'goDecision',
  'priority tier':                 'priorityTier',
  'summary':                       'summary',
  'research advantages':           'pros_text',   // handled specially
  'research considerations':       'cons_text',
  'flag / warning':                'flagWarning',
  'pharmacology: mechanism':       'pharmacology.mechanism',
  'pharmacology: primary target':  'pharmacology.primaryTarget',
  'pharmacology: onset/duration':  'pharmacology.onsetDuration',
  'pharmacology: metabolism':      'pharmacology.metabolism',
  'pk: oral bioavailability':      'pharmacology.bioavailability',
  'pk: tmax':                      'pharmacology.tmax',
  'pk: half-life':                 'pharmacology.halfLife',
  'lab synthesis: overview':       'labSynthesis.overview',
  'lab synthesis: route family':   'labSynthesis.routeFamily',
  'lab synthesis: steps':          'labSynthesis.steps',
  'lab synthesis: overall yield':  'labSynthesis.overallYield',
  'lab synthesis: analytics':      'labSynthesis.analytics',
  'kg mfg: overview':              'kgScaleSynthesis.overview',
  'kg mfg: feasibility':           'kgScaleSynthesis.feasibility',
  'kg mfg: target scale':          'kgScaleSynthesis.targetScale',
  'kg mfg: solvents used':         'kgScaleSynthesis.standardSolventsUsed',
  'kg mfg: solvents avoided':      'kgScaleSynthesis.standardSolventsNotUsed',
  'kg mfg: procedure (steps)':     'kgScaleSynthesis.steps',
  'kg mfg: hazards/ehs':           'kgScaleSynthesis.hazards',
  'kg mfg: yield target':          'kgScaleSynthesis.yieldTarget',
  'kg mfg: cost estimate ($/kg)':  'kgScaleSynthesis.costEstimate',
  'kg mfg: campaign timeline':     'kgScaleSynthesis.timeline',
  'safety: acute toxicity':        'safety.acuteToxicity',
  'safety: chronic risks':         'safety.chronicRisks',
  'safety: ehs classification':    'safety.ehsClassification',
  'safety: toxicology':            'safety.toxicology',
  'safety: side effects':          'safety.sideEffects',
  'safety: ppe requirements':      'safety.ppeRequired',
  'safety: contraindications':     'safety.contraindications',
  'ws: benefit score':             'wohlerScoring.benefit',
  'ws: harm score':                'wohlerScoring.harm',
  'ws: net value':                 'wohlerScoring.netValue',
  'ws: evidence confidence':       'wohlerScoring.evidenceConfidence',
  'ws: scoring rationale':         'wohlerScoring.rationale',
  'next steps / to-do':            'nextSteps',
  'key research questions':        'keyResearchQuestions',
  'notes':                         'internalNotes',
};

function setNestedField(obj, path, value) {
  const parts = path.split('.');
  if (parts.length === 1) {
    obj[parts[0]] = value;
  } else {
    if (!obj[parts[0]] || typeof obj[parts[0]] !== 'object') {
      obj[parts[0]] = {};
    }
    setNestedField(obj[parts[0]], parts.slice(1).join('.'), value);
  }
}

function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });

        if (!wb.SheetNames.includes('MASTER')) {
          reject(new Error('No MASTER sheet found in this workbook.'));
          return;
        }

        const ws = wb.Sheets['MASTER'];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!rows.length) {
          reject(new Error('MASTER sheet appears to be empty.'));
          return;
        }

        // Find the actual header row (first row with WOHLER_UID)
        // sheet_to_json uses first row as headers by default
        // Check if headers look right
        const firstRowKeys = Object.keys(rows[0]);
        const hasUID = firstRowKeys.some(k => String(k).trim().toUpperCase() === 'WOHLER_UID');

        let dataRows = rows;
        if (!hasUID) {
          // Try raw mode to find header row
          const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          const headerIdx = rawRows.findIndex(row =>
            row.some(cell => String(cell).trim().toUpperCase() === 'WOHLER_UID')
          );
          if (headerIdx === -1) {
            reject(new Error('Could not find WOHLER_UID column. Make sure you are using a Wohler export file.'));
            return;
          }
          const headers = rawRows[headerIdx];
          dataRows = rawRows.slice(headerIdx + 1).map(row => {
            const obj = {};
            headers.forEach((h, i) => { if (h) obj[String(h).trim()] = row[i] ?? ''; });
            return obj;
          });
        }

        // Parse each row into compound overrides
        const overrides = {};
        const preview = [];
        let skipped = 0;

        dataRows.forEach(row => {
          // Find the UID / slug
          const uid = String(row['WOHLER_UID'] || row['wohler_uid'] || '').trim();
          const slug = String(row['Compound ID (slug)'] || row['compound id (slug)'] || '').trim()
            || uid.toLowerCase().replace(/_/g, '-');

          if (!uid || !slug) { skipped++; return; }

          const compound = {};
          let fieldsChanged = 0;

          Object.entries(row).forEach(([rawCol, rawVal]) => {
            const col = rawCol.trim().toLowerCase();
            const path = COL_MAP[col];
            if (!path || path === 'skip') return;

            const val = String(rawVal ?? '').trim();
            if (!val) return; // skip blank cells

            let parsed;
            if (ARRAY_COLS.has(col)) {
              parsed = val.split('|').map(s => s.trim()).filter(Boolean);
            } else if (NUMERIC_COLS.has(col)) {
              const n = parseFloat(val);
              parsed = isNaN(n) ? val : n;
            } else {
              parsed = val;
            }

            // Special handling for pros/cons (stored as arrays but exported as pipe strings)
            if (path === 'pros_text') {
              compound.pros = val.split('|').map(s => s.trim()).filter(Boolean);
            } else if (path === 'cons_text') {
              compound.cons = val.split('|').map(s => s.trim()).filter(Boolean);
            } else {
              setNestedField(compound, path, parsed);
            }
            fieldsChanged++;
          });

          if (fieldsChanged === 0) { skipped++; return; }

          // Always carry id and categoryId
          compound.id = slug;
          if (compound.categoryId === undefined && row['Category ID']) {
            compound.categoryId = String(row['Category ID']).trim();
          }

          overrides[slug] = compound;
          preview.push({
            id: slug,
            name: compound.name || slug,
            fieldsChanged,
          });
        });

        resolve({ overrides, preview, skipped });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsArrayBuffer(file);
  });
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function Admin() {
  const [tab, setTab] = useState('export'); // 'export' | 'import' | 'manage' | 'team'
  useWorkSession(); // for future role checks

  // ── Team Management state ────────────────────────────────────────────
  const [teamUsers, setTeamUsers]       = useState([]);
  const [teamLoading, setTeamLoading]   = useState(false);
  const [teamMsg, setTeamMsg]           = useState(null);

  // Edit role inline
  const [editRoleUid, setEditRoleUid]   = useState(null);
  const [editRoleVal, setEditRoleVal]   = useState('viewer');

  // Invite form
  const [showInvite, setShowInvite]     = useState(false);
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteName, setInviteName]     = useState('');
  const [inviteRole, setInviteRole]     = useState('chemist');
  const [inviting, setInviting]         = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { email, resetLink, role }

  // Reset-link modal
  const [resetLinkData, setResetLinkData] = useState(null); // { email, resetLink }

  // Delete confirmation
  const [deleteUid, setDeleteUid]       = useState(null);

  const loadTeamUsers = useCallback(async () => {
    setTeamLoading(true);
    setTeamMsg(null);
    try {
      const users = await listUsers();
      setTeamUsers(users);
    } catch (e) {
      setTeamMsg({ type: 'error', text: e.message });
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'team') loadTeamUsers();
  }, [tab, loadTeamUsers]);

  const handleRoleUpdate = async (uid) => {
    try {
      await updateUserRole(uid, editRoleVal);
      setTeamMsg({ type: 'success', text: `Role updated to "${editRoleVal}"` });
      setEditRoleUid(null);
      loadTeamUsers();
    } catch (e) {
      setTeamMsg({ type: 'error', text: e.message });
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setTeamMsg(null);
    setInviteResult(null);
    try {
      const result = await inviteUser({ email: inviteEmail, displayName: inviteName, role: inviteRole });
      setInviteResult(result);
      setInviteEmail('');
      setInviteName('');
      loadTeamUsers();
    } catch (e) {
      setTeamMsg({ type: 'error', text: e.message });
    } finally {
      setInviting(false);
    }
  };

  const handleGetResetLink = async (uid) => {
    try {
      const data = await getResetLink(uid);
      setResetLinkData(data);
    } catch (e) {
      setTeamMsg({ type: 'error', text: e.message });
    }
  };

  const handleDelete = async (uid) => {
    try {
      await deleteUser(uid);
      setDeleteUid(null);
      setTeamMsg({ type: 'success', text: 'User deleted.' });
      loadTeamUsers();
    } catch (e) {
      setTeamMsg({ type: 'error', text: e.message });
    }
  };
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseResult, setParseResult] = useState(null); // { overrides, preview, skipped }
  const [parseError, setParseError] = useState(null);
  const [applied, setApplied] = useState(false);
  const [overrideStats, setOverrideStats] = useState(getOverrideStats());
  const [clearConfirm, setClearConfirm] = useState(false);
  const fileInputRef = useRef(null);

  const compoundCount = Object.keys(compoundsDB).length;

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    try {
      exportToExcel(compoundsDB);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  }, []);

  // ── Import file handling ──────────────────────────────────────────────────
  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setParseError('Please upload an .xlsx file (Excel format).');
      return;
    }
    setImporting(true);
    setParseError(null);
    setParseResult(null);
    setApplied(false);
    try {
      const result = await parseExcelFile(file);
      setParseResult(result);
    } catch (err) {
      setParseError(err.message);
    } finally {
      setImporting(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    processFile(file);
  }, [processFile]);

  const handleFileChange = useCallback((e) => {
    processFile(e.target.files[0]);
  }, [processFile]);

  // ── Apply import ──────────────────────────────────────────────────────────
  const handleApply = useCallback(() => {
    if (!parseResult) return;
    mergeOverrides(parseResult.overrides);
    setOverrideStats(getOverrideStats());
    setApplied(true);
  }, [parseResult]);

  // ── Clear overrides ───────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    clearOverrides();
    setOverrideStats({ compounds: 0, fields: 0 });
    setClearConfirm(false);
    setParseResult(null);
    setApplied(false);
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-hero">
        <div className="container">
          <div className="admin-hero-content">
            <div className="admin-hero-icon">⚙️</div>
            <div>
              <h1 className="admin-title">Data Management</h1>
              <p className="admin-subtitle">
                Export compound data to Excel · Edit with your team · Import changes back instantly
              </p>
            </div>
          </div>

          <div className="admin-stats-row">
            <div className="admin-stat">
              <span className="admin-stat-value">{compoundCount}</span>
              <span className="admin-stat-label">Compounds in Database</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-value" style={{ color: overrideStats.compounds > 0 ? '#34d399' : '#64748b' }}>
                {overrideStats.compounds}
              </span>
              <span className="admin-stat-label">Local Overrides Active</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-value" style={{ color: overrideStats.fields > 0 ? '#34d399' : '#64748b' }}>
                {overrideStats.fields}
              </span>
              <span className="admin-stat-label">Fields Overridden</span>
            </div>
          </div>

          <div className="admin-tabs">
            {['export', 'import', 'manage', 'team'].map(t => (
              <button
                key={t}
                className={`admin-tab ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'export' && '📥 Export to Excel'}
                {t === 'import' && '📤 Import from Excel'}
                {t === 'manage' && '🗂 Manage Overrides'}
                {t === 'team' && '👥 Team'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-body">
        <div className="container">

          {/* ── EXPORT TAB ── */}
          {tab === 'export' && (
            <div className="admin-panel">
              <h2 className="admin-panel-title">📥 Export Compound Database to Excel</h2>
              <p className="admin-panel-desc">
                Downloads a complete Excel workbook with every compound, every field.
                Share with your team — they edit it and send it back, then you import it here.
              </p>

              <div className="admin-workflow-steps">
                <div className="admin-step">
                  <div className="admin-step-num">1</div>
                  <div className="admin-step-text">
                    <strong>Click Export</strong><br />
                    Downloads <code>Wohler_Compounds_[date].xlsx</code> to your computer
                  </div>
                </div>
                <div className="admin-step-arrow">→</div>
                <div className="admin-step">
                  <div className="admin-step-num">2</div>
                  <div className="admin-step-text">
                    <strong>Edit in Excel / Google Sheets</strong><br />
                    Fill in any cells on the MASTER sheet. Blank cells are ignored on import.
                  </div>
                </div>
                <div className="admin-step-arrow">→</div>
                <div className="admin-step">
                  <div className="admin-step-num">3</div>
                  <div className="admin-step-text">
                    <strong>Import back here</strong><br />
                    Drag & drop the saved file on the Import tab. Changes go live immediately.
                  </div>
                </div>
              </div>

              <div className="admin-export-box">
                <div className="admin-export-info">
                  <div className="admin-export-icon">📊</div>
                  <div>
                    <div className="admin-export-filename">Wohler_Compounds_{new Date().toISOString().slice(0,10)}.xlsx</div>
                    <div className="admin-export-meta">
                      {compoundCount} compounds · MASTER sheet + INSTRUCTIONS · All fields included
                    </div>
                  </div>
                </div>
                <button className="admin-btn admin-btn-primary" onClick={handleExport}>
                  ⬇ Download Excel File
                </button>
              </div>

              <div className="admin-rules-box">
                <h3>Editing Rules</h3>
                <ul>
                  <li><strong>WOHLER_UID</strong> and <strong>Compound ID (slug)</strong> — never change these, they are the primary keys</li>
                  <li><strong>Blank cells</strong> — skipped on import; the existing value is kept</li>
                  <li><strong>Arrays</strong> (Aliases, Steps, Solvents, Next Steps) — separate items with a pipe character: <code>EtOAc | THF | MeOH</code></li>
                  <li><strong>References</strong> — format: <code>Title (https://url.com) | Title 2 (https://url2.com)</code></li>
                  <li><strong>Scores</strong> — plain numbers only (0–100)</li>
                  <li><strong>Status Color</strong> — must be: <code>green</code>, <code>orange</code>, or <code>red</code></li>
                </ul>
              </div>
            </div>
          )}

          {/* ── IMPORT TAB ── */}
          {tab === 'import' && (
            <div className="admin-panel">
              <h2 className="admin-panel-title">📤 Import Updates from Excel</h2>
              <p className="admin-panel-desc">
                Drop your edited Excel file below. Changes are previewed before applying —
                they take effect immediately in your browser with no rebuild or redeploy needed.
              </p>

              {/* Drop zone */}
              {!parseResult && !importing && (
                <div
                  className={`admin-dropzone ${dragOver ? 'drag-over' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="admin-dz-icon">📂</div>
                  <div className="admin-dz-text">
                    <strong>Drop your Excel file here</strong>
                    <span>or click to browse</span>
                  </div>
                  <div className="admin-dz-hint">.xlsx files only</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {/* Loading */}
              {importing && (
                <div className="admin-loading">
                  <div className="spinner" />
                  <span>Parsing Excel file…</span>
                </div>
              )}

              {/* Error */}
              {parseError && (
                <div className="admin-error-box">
                  <strong>⚠ Parse Error:</strong> {parseError}
                  <button className="admin-btn-link" onClick={() => { setParseError(null); }}>Try again</button>
                </div>
              )}

              {/* Preview */}
              {parseResult && !applied && (
                <div className="admin-preview">
                  <div className="admin-preview-header">
                    <h3>Preview — Ready to Import</h3>
                    <div className="admin-preview-counts">
                      <span className="admin-badge green">{parseResult.preview.length} compounds to update</span>
                      {parseResult.skipped > 0 && (
                        <span className="admin-badge gray">{parseResult.skipped} rows skipped (blank)</span>
                      )}
                    </div>
                  </div>

                  <div className="admin-preview-table-wrap">
                    <table className="admin-preview-table">
                      <thead>
                        <tr>
                          <th>Compound ID</th>
                          <th>Name</th>
                          <th>Fields to Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parseResult.preview.slice(0, 50).map(row => (
                          <tr key={row.id}>
                            <td><code>{row.id}</code></td>
                            <td>{row.name}</td>
                            <td>
                              <span className={`admin-badge ${row.fieldsChanged > 5 ? 'blue' : 'gray'}`}>
                                {row.fieldsChanged} field{row.fieldsChanged !== 1 ? 's' : ''}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {parseResult.preview.length > 50 && (
                          <tr>
                            <td colSpan={3} style={{ color: '#64748b', fontStyle: 'italic', textAlign: 'center' }}>
                              … and {parseResult.preview.length - 50} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="admin-preview-actions">
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={handleApply}
                    >
                      ✓ Apply Import ({parseResult.preview.length} compounds)
                    </button>
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => { setParseResult(null); setParseError(null); }}
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Success */}
              {applied && (
                <div className="admin-success-box">
                  <div className="admin-success-icon">✅</div>
                  <div>
                    <strong>Import applied successfully!</strong>
                    <p>
                      {parseResult?.preview.length} compounds updated · {getOverrideStats().fields} total fields now overridden.<br />
                      Changes are live in your browser. Navigate to any compound page to see them.
                    </p>
                  </div>
                  <div className="admin-success-actions">
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => { setParseResult(null); setApplied(false); setParseError(null); }}
                    >
                      Import Another File
                    </button>
                  </div>
                </div>
              )}

              {/* How it works note */}
              <div className="admin-info-note">
                <strong>How this works:</strong> Imported data is saved to your browser's local storage and merged
                on top of the compiled database. It persists across page refreshes but is browser-specific.
                To push changes permanently to all users, use the <code>python3 import_compounds.py</code> CLI
                tool and redeploy — or ask your developer to do so.
              </div>
            </div>
          )}

          {/* ── MANAGE TAB ── */}
          {tab === 'manage' && (
            <div className="admin-panel">
              <h2 className="admin-panel-title">🗂 Manage Local Overrides</h2>
              <p className="admin-panel-desc">
                Local overrides are edits imported via Excel that are stored in this browser.
                They are applied on top of the compiled database every time the page loads.
              </p>

              <div className="admin-manage-stats">
                <div className="admin-manage-stat-card">
                  <span className="val">{overrideStats.compounds}</span>
                  <span className="lbl">Compounds with overrides</span>
                </div>
                <div className="admin-manage-stat-card">
                  <span className="val">{overrideStats.fields}</span>
                  <span className="lbl">Total fields overridden</span>
                </div>
                <div className="admin-manage-stat-card">
                  <span className="val" style={{ fontSize: 13, color: overrideStats.compounds > 0 ? '#34d399' : '#64748b' }}>
                    {overrideStats.compounds > 0 ? 'Active' : 'None'}
                  </span>
                  <span className="lbl">Override status</span>
                </div>
              </div>

              {overrideStats.compounds > 0 && (
                <div className="admin-manage-detail">
                  <h3>Current Overrides (by Compound)</h3>
                  <div className="admin-override-list">
                    {Object.entries(loadOverrides()).map(([id, fields]) => (
                      <div key={id} className="admin-override-row">
                        <code className="admin-override-id">{id}</code>
                        <span className="admin-override-fields">
                          {Object.keys(fields).filter(k => k !== 'id' && k !== 'categoryId').join(', ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {overrideStats.compounds === 0 && (
                <div className="admin-empty-overrides">
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🧪</div>
                  <p>No local overrides active. The database is showing compiled data only.</p>
                </div>
              )}

              <div className="admin-manage-actions">
                {!clearConfirm ? (
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => setClearConfirm(true)}
                    disabled={overrideStats.compounds === 0}
                  >
                    🗑 Clear All Overrides
                  </button>
                ) : (
                  <div className="admin-confirm-box">
                    <span>Clear all {overrideStats.compounds} compound overrides? This cannot be undone.</span>
                    <button className="admin-btn admin-btn-danger" onClick={handleClear}>Yes, Clear All</button>
                    <button className="admin-btn admin-btn-secondary" onClick={() => setClearConfirm(false)}>Cancel</button>
                  </div>
                )}

                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => {
                    const data = JSON.stringify(loadOverrides(), null, 2);
                    const blob = new Blob([data], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `wohler_overrides_${new Date().toISOString().slice(0,10)}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={overrideStats.compounds === 0}
                >
                  ⬇ Export Overrides as JSON
                </button>
              </div>

              <div className="admin-info-note" style={{ marginTop: 24 }}>
                <strong>Note:</strong> Local overrides are stored in <em>this browser only</em>.
                They don't affect other team members' views. To permanently apply changes for everyone,
                the overrides need to be committed via the Python CLI tool and redeployed.
                Contact your developer or export the overrides JSON and send it along with the Excel file.
              </div>
            </div>
          )}

          {/* ── Team Tab ── */}
          {tab === 'team' && (
            <div className="admin-panel">
              <h2 className="admin-panel-title">👥 Team Management</h2>
              <p className="admin-panel-subtitle">
                Invite researchers, assign roles, and manage access to the portal.
              </p>

              {/* Role legend */}
              <div className="admin-role-legend">
                <span className="admin-role-badge admin-role-admin">admin</span>
                <span>Full access — edit all compounds, see Dashboard, manage team</span>
                <span className="admin-role-badge admin-role-chemist">chemist</span>
                <span>Inline editing on compound cards. Work sessions tracked.</span>
                <span className="admin-role-badge admin-role-viewer">viewer</span>
                <span>Read-only access.</span>
              </div>

              {/* Messages */}
              {teamMsg && (
                <div className={`admin-team-msg admin-team-msg-${teamMsg.type}`}>
                  {teamMsg.text}
                  <button className="admin-team-msg-close" onClick={() => setTeamMsg(null)}>✕</button>
                </div>
              )}

              {/* ── Invite form ── */}
              <div className="admin-invite-section">
                <button
                  className={`admin-btn ${showInvite ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                  onClick={() => { setShowInvite(v => !v); setInviteResult(null); setTeamMsg(null); }}
                >
                  {showInvite ? '✕ Cancel' : '+ Invite New User'}
                </button>

                {showInvite && (
                  <form className="admin-invite-form" onSubmit={handleInvite}>
                    <div className="admin-invite-fields">
                      <div className="admin-field-group">
                        <label>Email address *</label>
                        <input
                          type="email"
                          required
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          placeholder="chemist@example.com"
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-field-group">
                        <label>Display name</label>
                        <input
                          type="text"
                          value={inviteName}
                          onChange={e => setInviteName(e.target.value)}
                          placeholder="Dr. Jane Smith (optional)"
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-field-group">
                        <label>Role *</label>
                        <select
                          value={inviteRole}
                          onChange={e => setInviteRole(e.target.value)}
                          className="admin-role-select"
                        >
                          <option value="chemist">chemist</option>
                          <option value="viewer">viewer</option>
                          <option value="admin">admin</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="admin-btn admin-btn-primary"
                      disabled={inviting}
                    >
                      {inviting ? '⏳ Creating account…' : '📧 Create Account & Get Invite Link'}
                    </button>
                    <p className="admin-invite-note">
                      This creates a Firebase account and generates a <strong>password-setup link</strong> you can copy and send to them. They click it, set their password, and log in to wohler.ai.
                    </p>
                  </form>
                )}

                {/* Invite result — show the reset link to copy */}
                {inviteResult && (
                  <div className="admin-invite-result">
                    <div className="admin-invite-result-header">
                      ✅ Account {inviteResult.status === 'created' ? 'created' : 'updated'} for <strong>{inviteResult.email}</strong> with role <span className={`admin-role-badge admin-role-${inviteResult.role}`}>{inviteResult.role}</span>
                    </div>
                    <p className="admin-invite-result-label">Send this link to <strong>{inviteResult.email}</strong>:</p>
                    <div className="admin-link-box">
                      <span className="admin-link-text">{inviteResult.resetLink}</span>
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => { navigator.clipboard.writeText(inviteResult.resetLink); }}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <p className="admin-invite-note">
                      The link expires in 1 hour. You can generate a new one anytime with "🔗 New Link" in the table below.
                    </p>
                  </div>
                )}
              </div>

              {/* ── Reset link modal ── */}
              {resetLinkData && (
                <div className="admin-modal-overlay" onClick={() => setResetLinkData(null)}>
                  <div className="admin-modal" onClick={e => e.stopPropagation()}>
                    <div className="admin-modal-header">
                      🔗 Password / Invite Link for <strong>{resetLinkData.email}</strong>
                    </div>
                    <p>Send this link to the user. They click it to set (or reset) their password and are redirected to wohler.ai.</p>
                    <div className="admin-link-box">
                      <span className="admin-link-text">{resetLinkData.resetLink}</span>
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => navigator.clipboard.writeText(resetLinkData.resetLink)}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <p className="admin-invite-note">Link expires in 1 hour.</p>
                    <button className="admin-btn admin-btn-secondary" onClick={() => setResetLinkData(null)}>Close</button>
                  </div>
                </div>
              )}

              {/* ── Delete confirmation modal ── */}
              {deleteUid && (
                <div className="admin-modal-overlay" onClick={() => setDeleteUid(null)}>
                  <div className="admin-modal" onClick={e => e.stopPropagation()}>
                    <div className="admin-modal-header" style={{ color: '#f87171' }}>⚠️ Delete User</div>
                    <p>Are you sure you want to permanently delete this user? They will lose all access immediately. Their edit history in Firestore is preserved.</p>
                    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                      <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(deleteUid)}>Yes, Delete</button>
                      <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteUid(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Users table ── */}
              <div className="admin-team-header">
                <span style={{ color: '#94a3b8', fontSize: 13 }}>{teamUsers.length} user{teamUsers.length !== 1 ? 's' : ''}</span>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={loadTeamUsers} disabled={teamLoading}>
                  🔄 Refresh
                </button>
              </div>

              {teamLoading ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>⏳ Loading…</div>
              ) : teamUsers.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>No users found.</div>
              ) : (
                <table className="admin-team-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamUsers.map(u => (
                      <tr key={u.uid}>
                        <td>
                          <div className="admin-team-email">{u.email}</div>
                          {u.displayName && u.displayName !== u.email.split('@')[0] && (
                            <div className="admin-team-name">{u.displayName}</div>
                          )}
                        </td>
                        <td>
                          {editRoleUid === u.uid ? (
                            <div className="admin-role-edit">
                              <select
                                value={editRoleVal}
                                onChange={e => setEditRoleVal(e.target.value)}
                                className="admin-role-select"
                              >
                                <option value="admin">admin</option>
                                <option value="chemist">chemist</option>
                                <option value="viewer">viewer</option>
                              </select>
                              <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => handleRoleUpdate(u.uid)}>Save</button>
                              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setEditRoleUid(null)}>Cancel</button>
                            </div>
                          ) : (
                            <span className={`admin-role-badge admin-role-${u.role || 'viewer'}`}>{u.role || 'viewer'}</span>
                          )}
                        </td>
                        <td className="admin-team-date">
                          {u.lastLogin
                            ? new Date(u.lastLogin).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                            : <span style={{ color: '#475569' }}>Never</span>}
                        </td>
                        <td>
                          <div className="admin-team-actions-cell">
                            {editRoleUid !== u.uid && (
                              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => { setEditRoleUid(u.uid); setEditRoleVal(u.role || 'viewer'); setTeamMsg(null); }}>
                                ✏️ Role
                              </button>
                            )}
                            <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => handleGetResetLink(u.uid)}>
                              🔗 New Link
                            </button>
                            <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteUid(u.uid)}>
                              🗑
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
